import { type Dispatch, useState } from 'react';
import { Button } from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import { Modal } from '@johnlewispartnership/wtr-ingredients/ingredients/Modal';
import { type Product } from '@/services/products/types';
import { type TrolleyLineItem } from '@/types/api-types';
import { LineItemQuantityControls } from '@/components/LineItemQuantityControls/LineItemQuantityControls';

export interface AddToTrolleyButtonProps {
  product: Product;
  lineItem?: TrolleyLineItem;
  position: number | null | undefined;
  disabled?: boolean;
  loading?: boolean;
  buttonTheme?: 'primary' | 'secondary' | undefined;
  addProductToTrolley: (
    product: Product,
    quantity: number,
    position: number | null | undefined,
  ) => Promise<void>;
  updateProductQuantity: (lineItemId: string, productId: string, quantity: number) => Promise<void>;
  statusDisplayTime?: number;
  setInsufficientQuantity?: Dispatch<React.SetStateAction<boolean>>;
}

function AddToTrolleyButton({
  addProductToTrolley,
  buttonTheme = 'primary',
  disabled = false,
  lineItem,
  loading = false,
  position,
  product,
  setInsufficientQuantity,
  statusDisplayTime = 3000,
  updateProductQuantity,
}: AddToTrolleyButtonProps) {
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    'Product could not be added to your trolley.',
  );

  const addProduct = (_product: Product, _position: number | null | undefined) => {
    addProductToTrolley(_product, 1, _position).catch(() => {
      setIsErrorModalOpen(true);
      setErrorMessage('Product could not be added to your trolley.');
    });
  };

  return (
    <>
      {lineItem ? (
        <LineItemQuantityControls
          disabled={disabled}
          lineItem={lineItem}
          loading={loading}
          onUpdateError={() => {
            setIsErrorModalOpen(true);
            setErrorMessage('Product could not be updated.');
          }}
          setInsufficientQuantity={setInsufficientQuantity}
          statusDisplayTime={statusDisplayTime}
          updateProductQuantity={updateProductQuantity}
        />
      ) : (
        <Button
          aria-label={`Add ${product.name} to trolley`}
          data-testid="add-to-trolley-button"
          disabled={disabled}
          onClick={() => addProduct(product, position)}
          text="Add"
          type="button"
          variant={buttonTheme}
          waiting={loading}
          width="full"
        />
      )}

      {isErrorModalOpen && (
        <Modal
          buttons={[
            {
              buttonText: 'Close',
            },
          ]}
          handleModalClose={() => setIsErrorModalOpen(false)}
          isOpen={isErrorModalOpen}
          titleText={errorMessage}
        >
          Something has gone wrong at our end. Please accept our apologies and try again.
        </Modal>
      )}
    </>
  );
}

export default AddToTrolleyButton;
